/** Platform selection, draft and request lifetime (STUDIO-HOME-004, QA226).
 * Figma Platform 3:250947, Studio 3:251047, AVP 3158:105322.
 */

import React from "react";
import { Box, Button, ButtonBase, IconButton, Typography } from "@mui/material";

import DSDialog from "./design-system/DSDialog";
import Icon from "./Icon";
import designTokens, { colors, numbers, typography } from "../styles/designTokens";
import {
  REPORT_SYMPTOMS,
  REPORT_SYMPTOM_LABELS,
  submitStudioReport,
  type ReportSymptom,
  type ReportPlatform,
  type ReportDraft,
} from "../services/api/reportIssue";
import { type ReportIssueEntry } from "../store/reportIssueStore";
import { ApiError } from "../services/api/base";

export interface ReportIssueModalProps {
  open: boolean;
  entry?: ReportIssueEntry;
  sessionId?: number;
  onClose: () => void;
  /** 전송이 끝난 시점. 홈의 에러 토스트를 내리는 쪽이 듣는다. */
  onSent?: () => void;
}

type Phase = "platform" | "symptoms" | "sent";

const MODAL_WIDTH = 500;
const DESCRIPTION_MAX_LENGTH = 4000;

/**
 * 실패는 토스트로 알리지 않는다. 모달을 유지하고 그 자리에서 알린 뒤 다시
 * 보낼 수 있게 한다. 모달이 닫히면 사용자가 쓴 내용이 사라진다.
 */
const failureMessage = (error: unknown): string => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "Sending the report failed. No internet connection. Check your network and try again.";
  }
  if (error instanceof ApiError && error.status === 429) {
    return "Sending the report failed. Too many reports were sent just now. Wait a minute and try again.";
  }
  return "Sending the report failed. Server error. Please try again.";
};

const ReportIssueModal: React.FC<ReportIssueModalProps> = ({
  open,
  entry = "sidebar",
  sessionId = 0,
  onClose,
  onSent,
}) => {
  const [phase, setPhase] = React.useState<Phase>(entry === "toast" ? "symptoms" : "platform");
  const [platform, setPlatform] = React.useState<ReportPlatform | null>(entry === "toast" ? "studio" : null);
  const [selected, setSelected] = React.useState<ReportSymptom[]>([]);
  const [description, setDescription] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [failure, setFailure] = React.useState<string | null>(null);

  const attemptRef = React.useRef(0);
  const inFlightRef = React.useRef(false);
  const activeRef = React.useRef(false);
  const currentRef = React.useRef({ open, sessionId, entry });
  currentRef.current = { open, sessionId, entry };

  React.useLayoutEffect(() => {
    activeRef.current = open;
    attemptRef.current += 1;
    inFlightRef.current = false;
    setPhase(entry === "toast" ? "symptoms" : "platform");
    setPlatform(entry === "toast" ? "studio" : null);
    setSelected([]);
    setDescription("");
    setSending(false);
    setFailure(null);
    return () => {
      activeRef.current = false;
      attemptRef.current += 1;
      inFlightRef.current = false;
    };
  }, [open, entry, sessionId]);

  const close = () => {
    activeRef.current = false;
    attemptRef.current += 1;
    onClose();
  };
  const back = () => {
    if (inFlightRef.current) return;
    attemptRef.current += 1;
    setSelected([]);
    setDescription("");
    setFailure(null);
    setPlatform(null);
    setPhase("platform");
  };
  const symptoms = platform ? REPORT_SYMPTOMS[platform] : [];

  const hasOther = selected.includes("other");
  const canSend =
    phase === "symptoms" && platform !== null && selected.length > 0 &&
    (!hasOther || description.trim().length > 0) &&
    !sending;

  const toggle = (symptom: ReportSymptom) => {
    if (inFlightRef.current) return;
    setFailure(null);
    setSelected((previous) =>
      previous.includes(symptom)
        ? previous.filter((code) => code !== symptom)
        : // 화면에 보이는 순서를 유지한다. 고른 순서로 쌓지 않는다.
          symptoms.filter(
            (code) => code === symptom || previous.includes(code),
          ),
    );
  };

  const send = async () => {
    if (!canSend || !platform || inFlightRef.current || !activeRef.current) return;
    inFlightRef.current = true;
    const attemptId = ++attemptRef.current;
    const isCurrent = () => activeRef.current && currentRef.current.open &&
      currentRef.current.sessionId === sessionId && currentRef.current.entry === entry &&
      attemptRef.current === attemptId;
    // Selection only comes from this platform's allowlist; snapshot before awaiting.
    const draft = { platform, symptoms: [...selected], description } as ReportDraft;
    setSending(true);
    setFailure(null);
    try {
      await submitStudioReport(draft);
      if (!isCurrent()) return;
      setPhase("sent");
      onSent?.();
    } catch (error) {
      if (isCurrent()) setFailure(failureMessage(error));
    } finally {
      if (isCurrent()) {
        inFlightRef.current = false;
        setSending(false);
      }
    }
  };

  const titleId = "report-issue-title";

  return (
    <DSDialog
      open={open}
      onClose={close}
      size="md"
      aria-labelledby={titleId}
      paperSx={{ width: MODAL_WIDTH }}
    >
      <Box aria-hidden="true" sx={{ width: "100%", height: numbers.gap[16] }} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: numbers.gap[64],
          px: numbers.gap[32],
          gap: numbers.gap[8],
        }}
      >
        <Typography
          id={titleId}
          sx={{
            ...typography.styles.viewrStudio.headingMedium,
            color: colors.common[100].dark,
            flex: 1,
            minWidth: 0,
          }}
        >
          {phase === "sent" ? "Issue reported" : "Report an issue"}
        </Typography>
        <IconButton
          aria-label="Close report issue"
          onClick={close}
          sx={{
            width: 24,
            height: 24,
            p: 0,
            color: colors.grey[60].dark,
            "&:hover": { bgcolor: "transparent" },
          }}
        >
          <Icon name="close-24" aria-hidden="true" />
        </IconButton>
      </Box>

      {phase === "platform" ? (
        <>
          <Box sx={{ px: numbers.gap[32], pb: numbers.gap[16] }}>
            <Typography sx={{ ...typography.styles.viewrStudio.headline1Regular, color: colors.grey[90].dark }}>
              Which platform had the issue?
            </Typography>
          </Box>
          <Box role="radiogroup" aria-label="Issue platform" sx={{ display: "flex", gap: numbers.gap[16], px: numbers.gap[32], pb: numbers.gap[8] }}>
            {(["studio", "avp"] as const).map((value, index) => (
              <ButtonBase
                key={value}
                role="radio"
                aria-checked={platform === value}
                aria-label={value === "studio" ? "View R Studio" : "Vision Pro"}
                tabIndex={platform === value || (platform === null && index === 0) ? 0 : -1}
                onClick={() => setPlatform(value)}
                onKeyDown={(event) => {
                  if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
                  event.preventDefault();
                  const next = event.key === "Home" ? "studio" : event.key === "End" ? "avp" : value === "studio" ? "avp" : "studio";
                  setPlatform(next);
                  const group = event.currentTarget.parentElement;
                  (group?.querySelectorAll<HTMLElement>('[role="radio"]')[next === "studio" ? 0 : 1])?.focus();
                }}
                sx={{
                  flex: 1, minWidth: 0, height: 110, flexDirection: "column", alignItems: "flex-start", px: numbers.gap[24], gap: numbers.gap[12],
                  border: "1px solid", borderRadius: numbers.cornerRadius[12],
                  borderColor: platform === value ? colors.primary[50].dark : "transparent",
                  bgcolor: designTokens.fill.shape.grey30.dark,
                  color: colors.common[100].dark,
                  "&:hover": { borderColor: colors.primary[50].dark },
                  "&.Mui-focusVisible": { outline: `2px solid ${colors.primary[50].dark}`, outlineOffset: 2 },
                }}
              >
                <Icon name={value === "studio" ? "global-24" : "visionrpro-24"} />
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: numbers.gap[4] }}>
                  <Typography sx={typography.styles.viewrStudio.label1Medium}>{value === "studio" ? "View R Studio" : "View R"}</Typography>
                  <Typography sx={{ ...typography.styles.viewrStudio.captionRegular, color: colors.grey[80].dark }}>{value === "studio" ? "Web platform" : "Vision Pro"}</Typography>
                </Box>
              </ButtonBase>
            ))}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", height: numbers.gap[64], px: numbers.gap[32] }}>
            <Button variant="contained" disabled={!platform} onClick={() => { if (platform) setPhase("symptoms"); }} sx={{
              height: 48, minWidth: 100, px: numbers.gap[12],
              bgcolor: colors.primary[50].dark, color: colors.common[100].dark,
              borderRadius: numbers.cornerRadius[8], textTransform: "none", boxShadow: "none",
              ...typography.styles.viewrStudio.bodyRegular,
              "&:hover": { bgcolor: designTokens.semantic.button.primaryAction.background.hover.dark, boxShadow: "none" },
            }}>Next</Button>
          </Box>
        </>
      ) : phase === "symptoms" ? (
        <>
          <Box sx={{ px: numbers.gap[32], pb: numbers.gap[24] }}>
            <Typography
              sx={{
                ...typography.styles.viewrStudio.headline1Regular,
                color: colors.grey[90].dark,
              }}
            >
              Select all that apply.
            </Typography>
          </Box>

          <Box
            data-testid="report-issue-options"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: numbers.gap[12],
              px: numbers.gap[32],
              pb: numbers.gap[32],
            }}
          >
            {symptoms.map((symptom) => {
              const checked = selected.includes(symptom);
              return (
                <Box
                  key={symptom}
                  sx={{
                    display: "flex",
                    alignItems: symptom === "other" ? "flex-start" : "center",
                    gap: numbers.gap[8],
                    width: "100%",
                  }}
                >
                  <ButtonBase
                    role="checkbox"
                    disabled={sending}
                    aria-checked={checked}
                    aria-label={REPORT_SYMPTOM_LABELS[symptom]}
                    data-symptom={symptom}
                    onClick={() => toggle(symptom)}
                    sx={{
                      width: 24,
                      height: 24,
                      flexShrink: 0,
                      borderRadius: numbers.cornerRadius[4],
                      color: checked
                        ? colors.primary[50].dark
                        : colors.grey[60].dark,
                    }}
                  >
                    <Icon
                      name={checked ? "checkbox-solid-24" : "checkbox-empty-24"}
                      aria-hidden="true"
                    />
                  </ButtonBase>

                  {symptom === "other" ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: numbers.gap[4],
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        onClick={() => toggle(symptom)}
                        sx={{
                          ...typography.styles.viewrStudio.label2Regular,
                          color: colors.grey[80].dark,
                          height: 24,
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        {REPORT_SYMPTOM_LABELS[symptom]}
                      </Typography>
                      {checked && (
                        <Box
                          component="input"
                          data-testid="report-issue-description"
                          aria-label="Describe what happened"
                          value={description}
                          maxLength={DESCRIPTION_MAX_LENGTH}
                          disabled={sending}
                          autoFocus
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            if (inFlightRef.current) return;
                            setFailure(null);
                            setDescription(event.target.value);
                          }}
                          placeholder="Describe what happened."
                          sx={{
                            height: 40,
                            width: "100%",
                            border: "none",
                            outline: "none",
                            px: numbers.gap[8],
                            borderRadius: numbers.cornerRadius[8],
                            bgcolor: designTokens.fill.shape.opacityWhite10.dark,
                            color: colors.grey[90].dark,
                            ...typography.styles.viewrStudio.label2Regular,
                            "&::placeholder": { color: colors.grey[70].dark },
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    <Typography
                      onClick={() => toggle(symptom)}
                      sx={{
                        ...typography.styles.viewrStudio.label2Regular,
                        color: colors.grey[80].dark,
                        cursor: "pointer",
                      }}
                    >
                      {REPORT_SYMPTOM_LABELS[symptom]}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {failure && (
            <Box sx={{ px: numbers.gap[32], pb: numbers.gap[12] }}>
              <Typography
                role="alert"
                data-testid="report-issue-failure"
                sx={{
                  ...typography.styles.viewrStudio.label2Regular,
                  color: colors.red[50].dark,
                }}
              >
                {failure}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: numbers.gap[24],
              height: numbers.gap[64],
              px: numbers.gap[32],
            }}
          >
            <Button
              variant="outlined"
              disabled={platform === "avp" && sending}
              onClick={platform === "avp" ? back : close}
              sx={{
                height: 48,
                minWidth: 100,
                px: numbers.gap[12],
                color: colors.grey[80].dark,
                borderColor: colors.grey[60].dark,
                borderRadius: numbers.cornerRadius[8],
                textTransform: "none",
                boxShadow: "none",
                ...typography.styles.viewrStudio.bodyRegular,
              }}
            >
              {platform === "avp" ? "Back" : "Cancel"}
            </Button>
            <Button
              variant="contained"
              disabled={!canSend}
              onClick={send}
              sx={{
                height: 48,
                minWidth: 100,
                px: numbers.gap[12],
                bgcolor: colors.primary[50].dark,
                color: colors.common[100].dark,
                borderRadius: numbers.cornerRadius[8],
                textTransform: "none",
                boxShadow: "none",
                ...typography.styles.viewrStudio.bodyRegular,
                "&:hover": {
                  bgcolor:
                    designTokens.semantic.button.primaryAction.background.hover
                      .dark,
                  boxShadow: "none",
                },
              }}
            >
              Send report
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ px: numbers.gap[32], pb: numbers.gap[24] }}>
            <Typography
              sx={{
                ...typography.styles.viewrStudio.headline1Regular,
                color: colors.grey[90].dark,
              }}
            >
              Report sent. We&apos;ll look into it.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              height: numbers.gap[64],
              px: numbers.gap[32],
            }}
          >
            <Button
              variant="contained"
              onClick={close}
              sx={{
                height: 48,
                minWidth: 100,
                px: numbers.gap[12],
                bgcolor: colors.primary[50].dark,
                color: colors.common[100].dark,
                borderRadius: numbers.cornerRadius[8],
                textTransform: "none",
                boxShadow: "none",
                ...typography.styles.viewrStudio.bodyRegular,
                "&:hover": {
                  bgcolor:
                    designTokens.semantic.button.primaryAction.background.hover
                      .dark,
                  boxShadow: "none",
                },
              }}
            >
              Done
            </Button>
          </Box>
        </>
      )}

      <Box aria-hidden="true" sx={{ width: "100%", height: numbers.gap[16] }} />
    </DSDialog>
  );
};

export default ReportIssueModal;
